import React from 'react';
import { Paper, Box, Typography, Grid, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const DisbursementChart = ({ project, collapsed, onToggleCollapse }) => {
  if (!project) return null;

  const getChartData = () => {
    if (!project.rooms) return [];
    let totalDisbursed = 0;
    let totalBudget = 0;
    project.rooms.forEach(room => {
      room.items.forEach(item => {
        if (item.status !== 'pending_homeowner') {
          totalDisbursed += parseFloat(item.amount_disbursed || 0);
          totalBudget += parseFloat(item.cost);
        }
      });
    });
    return [
      { name: 'Disbursed', value: totalDisbursed, color: '#4caf50' },
      { name: 'Remaining', value: totalBudget - totalDisbursed, color: '#ff9800' },
    ];
  };

  const chartData = getChartData();

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={collapsed ? 0 : 2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5">Funds Disbursement</Typography>
          {collapsed && (
            <Box display="flex" gap={2}>
              <Typography variant="h6" color="success.main" fontWeight="bold">
                ${chartData[0]?.value.toLocaleString() || '0'} Disbursed
              </Typography>
              <Typography variant="h6" color="warning.main" fontWeight="bold">
                ${chartData[1]?.value.toLocaleString() || '0'} Remaining
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton onClick={onToggleCollapse} aria-label={collapsed ? 'Expand disbursement chart' : 'Collapse disbursement chart'}>
          <ExpandMoreIcon sx={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: '0.3s' }} />
        </IconButton>
      </Box>
      {!collapsed && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box height={250}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: $${value.toLocaleString()}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#e8f5e9' }}>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  ${chartData[0]?.value.toLocaleString() || '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">Disbursed</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fff3e0' }}>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  ${chartData[1]?.value.toLocaleString() || '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">Remaining</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6">
                  {chartData[0]?.value > 0
                    ? `${((chartData[0].value / (chartData[0].value + chartData[1].value)) * 100).toFixed(1)}%`
                    : '0%'}
                </Typography>
                <Typography variant="body2" color="text.secondary">Disbursement Progress</Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default DisbursementChart;
